import React, { useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Environments, IntlProvider, SelectedArtefactType } from "@emisgroup/cohort-builder-kit";
import { CohortBuilder } from "@emisgroup/cohort-builder";
import { useTranslation } from "@emisgroup/acp-application-intl";
import LeftPanelView from "../LeftPanel/LeftPanel.view";
import { ErrorAlert } from "../ErrorAlert/ErrorAlert";
import { ProgressSpinner } from "../ProgressSpinner/ProgressSpinner";
import { IRootState } from "../../reducer/CombineReducer";
import { useUser } from "../../AuthContext";
import { AppBanner } from "../AppBanner/AppBanner";
import { APP_EXTRACT, FEASIBILITY_CODELIST } from "../AppConfig/Constant";
import { AppConfigSlice } from "../AppConfig/Redux/Index";
import { AppConfig } from "../AppConfig/AppConfig";
import { getAppKey, setAppKey } from "../AppConfig/Helper";
import { ErrorAlertSlice } from "../ErrorAlert/Index";
import { ExtractListSlice } from "../LeftPanel/ExtractList";
import { FinancialSlice } from "../LeftPanel/FinancialYearList";
import { extractSelectedType } from "../LeftPanel/ExtractList/redux/Types";
import { setActiveTab } from "../Common/TreeList/Helper/Script";
import ExtractDetail from "../RightPanel/ExtractDetail/ExtractDetail.logic";
import { FEASIBILITY_APP_ID } from "../LeftPanel/Constant";
import { ErrorAlertType, IErrorAlertObject } from "../ErrorAlert/redux/Types";
import { ProgressSpinnerSlice } from "../ProgressSpinner/Index";
import { ButtonVariantType } from "../Common/UI/Types";
import { ExecutionList } from "../RightPanel/Execution/ExecutionList/ExecutionList.logic";
import { ResultDetail } from "../RightPanel/Results/ResultDetail/ResultDetail";
import { EmptyExtract } from "./EmptyExtract";
import "@emisgroup/cohort-builder/build/dist/src/components/IndexLib.css";
import { UserService } from "./Service";
import "./Style.scss";

export const ClipBoardStorage = "clipboard-storage";

/**
 * To reset/remove the copied rule in clipboard-storage in session storage
 */
export const clearClipboardStorage = (): void => {
  sessionStorage.removeItem(ClipBoardStorage);
};

/**
 * Responsible for rendering the home component
 */
const Home: React.FC = () => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const [
    extractState,
    yearsState,
    partition,
  ] = useSelector((state: IRootState) => [
    state.extractState,
    state.yearsState,
    state.partition,
    state.appConfig
  ], shallowEqual);
  const selectedExtract = extractState.selectedExtract;
  const extractGuid = selectedExtract?.extractGuid as string;
  const {
    selectedId,
    artefacts,
    breadcrumbItems,
    parentData,
    metaData
  } = partition;
  const { user, logout } = useUser();
  const token = user.token;
  const isCollapsedView = yearsState.isCollapsedView;
  const className = isCollapsedView ? "panel-collapsed" : "panel-expanded";
  const classNames = isCollapsedView ? "qms-home-container--collapsed" : "qms-home-container--expanded";
  const { clickedExtractId } = extractState;
  const [libraryData, setLibraryData] = useState<SelectedArtefactType>();
  const [authorized, setAuthorized] = useState<boolean>(true);

  /**
   * Call back method to be triggered when switching to other extract when reports edited
   * @param status - flag value to get other extract details
   */
  const getReportStatus = (
    status: boolean,
  ): void => {
    const extractId = clickedExtractId?.id || extractGuid;
    if (!status && extractId) {
      const year = clickedExtractId?.year || selectedExtract?.financialYear;
      try {
        dispatch(ExtractListSlice.actions.showExtractReports(true));
        dispatch(FinancialSlice.actions.selectedYear(year as string));
        dispatch(ExtractListSlice.actions.setClickedExtractIdDetails({} as extractSelectedType));
      } catch (ex) {
        dispatch(ErrorAlertSlice.actions.showAlert(true));
      }
    }
  };
  /**
   * Call back method once the yes option in warning is done from QM
   * @param status - flag value to get extract detail of clicked next extract
   */
  const getReportCompleted = (status: boolean): void => {
    const extractId = clickedExtractId?.id || extractGuid;
    const year = clickedExtractId?.year || selectedExtract?.financialYear;
    if (status) {
      if (extractId) {
        try {
          dispatch(ExtractListSlice.actions.showExtractReports(true));
          dispatch(FinancialSlice.actions.selectedYear(year as string));
          setActiveTab();
        } catch (ex) {
          dispatch(ErrorAlertSlice.actions.showAlert(true));
        }
      }
      dispatch(ExtractListSlice.actions.setClickedExtractIdDetails({} as extractSelectedType));
    }
  };

  React.useEffect(() => {
    // To remove/reset the clipboard storage at initial render
    const appKey = getAppKey();
    clearClipboardStorage();
    setAppKey(appKey ? appKey : APP_EXTRACT);
    dispatch(AppConfigSlice.actions.setTabListData(AppConfig.MenuList));
    dispatch(AppConfigSlice.actions.setAplicationKey(appKey));
  }, []);

  // To update the library data
  React.useEffect(() => {
    const library = artefacts.find((obj: SelectedArtefactType) => obj["name"] === "Library");
    setLibraryData(library);
  }, [artefacts]);

  /**
   * For updating selected Extract details id
   * @param id - selected extract details id
   */
  const setOpenExtractDetails = (id: string): void => {
    dispatch(ExtractListSlice.actions.setSelectedExtractGuid(id));
  };

  const showAuthorizationError = (title?: string, message?: string): void => {
    const errorContent: IErrorAlertObject = {
      title: title ?? translate("Alert.Title.Default"),
      message: [message ?? translate("Alert.Message.Default")] ,
      type: ErrorAlertType.ERROR,
      actionButtonText: translate("Button.LogoutButton"),
      actionClick: () => logout(),
      buttonVarient: ButtonVariantType.Danger,
    };
    dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
    dispatch(ErrorAlertSlice.actions.createErrorAlert(errorContent));
  };

  const authorize = async (): Promise<void> => {
    try {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.CheckUserAuthorization")));
      const response = await UserService.authorize(FEASIBILITY_APP_ID);
      if (response && response?.data?.payload?.message === "Authorization successful") {
        setAuthorized(true);
        dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
      } else {
        showAuthorizationError(translate("UnauthorizedError.Title"), translate("UnauthorizedError.Message"));
      }
    } catch(ex){
      showAuthorizationError(ex.data?.message, ex.data?.errors[0]?.detail[0]?.msg);
    }
  };

  React.useEffect(() => {
    void authorize();
  }, []);

  return (
    <div className="ebt-home">
      <IntlProvider>
        {authorized && (
          <>
            <AppBanner />
            <div className={`ebt-builder_landing ${className}`} data-testid="ebt-builder">
              <LeftPanelView />
              {!selectedId ?
                <EmptyExtract />
                :
                <div className={classNames}>
                  <CohortBuilder
                    appConfig={AppConfig}
                    appKey={"feasibilityApp"}
                    authToken={token}
                    breadcrumbItems={breadcrumbItems}
                    codeListIds={FEASIBILITY_CODELIST}
                    customTabContent={extractState.showExtractDetails ? <ExtractDetail /> : undefined}
                    extractGuid={selectedId}
                    extractName={extractState?.dashBoardName}
                    getReportCompleted={getReportCompleted}
                    getReportStatus={getReportStatus}
                    libraryData={libraryData as SelectedArtefactType}
                    parentData={parentData}
                    logout={logout}
                    openExtractDetails={setOpenExtractDetails}
                    serviceEnv={process.env.APP_TARGET_ENV as Environments}
                    openedExtractId={extractState.selectedExtractGuid}
                    metaData={metaData}
                    artefactList={artefacts}
                    applicationId={FEASIBILITY_APP_ID}
                    customExecuteTab={<ExecutionList/>}
                    customResultsTab={<ResultDetail/>}
                  />
                </div>
              }
            </div>
          </>
        )}
        <ErrorAlert />
        <ProgressSpinner />
      </IntlProvider>
    </div>
  );
};

export default Home;
